import { useEffect } from "react";

import { ArticleQuery, AuthorBioSwanFragment } from "types/api";
import lazy from "utils/lazy";

import ArticleWrapper from "components/article/ArticleWrapper";
import ArticleBentoRecirc from "components/article/ArticleBentoRecirc";
import ArticleHeader from "components/article/ArticleHeader";
import ArticleLockup from "components/article/ArticleLockup";
import ArticleUtilityBar from "components/article/ArticleUtilityBar";
import ArticleWell from "components/article/ArticleWell";
import ArticleBody from "components/article/ArticleBody";

import ArticleRubric from "components/article/ArticleRubric";
import ArticleTitle from "components/article/ArticleTitle";
import ArticleDek from "components/article/ArticleDek";
import ArticleBylines from "components/article/ArticleBylines";
import ArticleLeadArt from "components/article/ArticleLeadArt";
import AboutTheAuthors from "components/article/AboutTheAuthors";
import ArticleTimestamp from "components/article/ArticleTimestamp";
import ArticleShare from "components/article/ArticleShare";
import EditorsNote from "components/article/EditorsNote";
import LeaderboardAd from "../ads/Leaderboard";
import ArticleRelatedVideos from "components/article/ArticleRelatedVideos";

const ArticleFoundationLine = lazy(
  () => import("components/article/ArticleFoundationLine")
);
const ArticleBio = lazy(() => import("components/article/ArticleBio"));
const ArticleMostPopular = lazy(
  () => import("components/article/ArticleMostPopular")
);

const ArticleLayout = (article: ArticleQuery["article"]) => {
  // add any embed script tags to the page, if needed
  useEffect(() => {
    if (article.embeds.length) {
      for (const embed of article.embeds) {
        if (embed?.type === "script") {
          const scriptEl = document.createElement("script");
          scriptEl.type = "text/javascript";
          scriptEl.src = embed.src;
          document.body.appendChild(scriptEl);
        }
      }
    }
  }, [article.embeds]);

  return (
    <main id="main-content" data-category="story page">
      {article.editorialProject &&
      article.editorialProject.river?.edges?.length > 3 ? (
        <ArticleBentoRecirc {...article.editorialProject} />
      ) : (
        // This ad and Bento should not appear at the same
        // time because together they take up 700px of space above
        // the article
        <LeaderboardAd />
      )}

      <ArticleWrapper layout={article.layout}>
        <ArticleHeader>
          {article.layout === "feature" && article.leadArt && (
            <ArticleLeadArt layout={article.layout} {...article.leadArt} />
          )}
          <ArticleLockup>
            <ArticleRubric {...article} />
            <ArticleTitle {...article} />
            <ArticleDek {...article} />
            <ArticleBylines {...article} />
          </ArticleLockup>
          {article.layout === "standard" && article.leadArt && (
            <ArticleLeadArt layout={article.layout} {...article.leadArt} />
          )}
          <ArticleUtilityBar {...article}>
            <ArticleTimestamp {...article} />
            <ArticleShare {...article} />
          </ArticleUtilityBar>
          <ArticleWell borderBottom>
            {/* Display the "About The Authors" module for non-magazine/web-only Ideas stories */}
            {article.primaryChannel?.slug === "ideas" &&
              article.__typename === "BlogArticle" && (
                <AboutTheAuthors {...article} />
              )}
            {article.editorsNote && <EditorsNote {...article.editorsNote} />}
          </ArticleWell>
        </ArticleHeader>
        <ArticleBody {...article} />
        <div id="article-end" style={{ height: `1px` }} />
        <ArticleWell>
          {article.editorialProject?.foundationLineArticle && (
            <ArticleFoundationLine {...article.editorialProject} />
          )}
        </ArticleWell>
        <ArticleRelatedVideos
          id={article.id.split(":")[1]}
          preview={article.preview}
        />
        {article.authors &&
          article.authors.map((author: AuthorBioSwanFragment, index) => {
            return (
              <ArticleBio key={author.displayName} index={index} {...author} />
            );
          })}
      </ArticleWrapper>
      <ArticleMostPopular />
    </main>
  );
};

export default ArticleLayout;
